
import { useState, useCallback } from "react";
import { Employee, EmployeeFormData } from "@/components/admin/employees/types";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEmployeeCreation } from "./employee/useEmployeeCreation";
import { useEmployeeProfile } from "./employee/useEmployeeProfile";
import { useEmployeeRole } from "./employee/useEmployeeRole";

const SUPABASE_URL = "https://cfejunmfrwocqhtatxwz.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNmZWp1bm1mcndvY3FodGF0eHd6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzYzNzE0MDUsImV4cCI6MjA1MTk0NzQwNX0.5kDzX_q4GyhE3iz4PlS1o4mtVemN-x_5af2SHpwu7S0";

const initialFormData: EmployeeFormData = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  street: "",
  city: "",
  state: "",
  zip_code: "",
  password: "",
  role: "user"
};

export const useEmployeeForm = (employee: Employee | null, isNewEmployee: boolean) => {
  const [formData, setFormData] = useState<EmployeeFormData>(
    employee
      ? {
          first_name: employee.first_name || "",
          last_name: employee.last_name || "",
          email: employee.email || "",
          phone: employee.phone || "", // Ensure phone is initialized
          street: employee.street || "",
          city: employee.city || "",
          state: employee.state || "",
          zip_code: employee.zip_code || "",
          password: "",
          role: employee.role || "user"
        }
      : initialFormData
  );
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { createEmployee } = useEmployeeCreation();
  const { createProfile, updateProfile } = useEmployeeProfile();
  const { setUserRole, updateUserRole } = useEmployeeRole();

  const handleFieldChange = useCallback((field: keyof EmployeeFormData, value: string) => {
    console.log(`Setting ${field} to:`, value);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    console.log("Form data being submitted:", formData); // Add this for debugging
    console.log("Phone number in form data:", formData.phone); // Debug phone number

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("No active session");
      }

      // Check if current user has admin role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (roleError) throw roleError;
      if (roleData?.role !== 'admin') {
        throw new Error("Admin privileges required");
      }

      if (isNewEmployee) {
        // Call the Edge Function to create employee
        const response = await fetch(`${SUPABASE_URL}/functions/v1/create-employee`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password || Math.random().toString(36).slice(-8),
            first_name: formData.first_name,
            last_name: formData.last_name,
            phone: formData.phone || '', // Ensure phone is included and sent
            role: formData.role,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to create employee');
        }

        const { user } = await response.json();
        if (!user) throw new Error('No user data returned');

        // Double check profile creation with phone
        console.log("Creating full profile with phone:", formData.phone);
        const profileResult = await createProfile(user.id, {
          ...formData,
          phone: formData.phone || '', // Ensure phone is never null
        });
        
        if (!profileResult.success) throw profileResult.error;

        toast({
          title: "Success",
          description: "Employee created successfully",
        });

        return true;
      } else {
        const employeeData: Employee = {
          id: employee?.id || "",
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone || '', // Ensure phone is never null
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          role: formData.role
        };

        console.log("Updating employee with phone:", employeeData.phone);
        const profileResult = await updateProfile(employeeData);
        if (!profileResult.success) throw profileResult.error;

        const roleResult = await updateUserRole(employeeData.id, employeeData.role);
        if (!roleResult.success) throw roleResult.error;

        toast({
          title: "Success",
          description: "Employee updated successfully",
        });

        return true;
      }
    } catch (error: any) {
      console.error("Error in handleSubmit:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || (isNewEmployee ? "Failed to create employee" : "Failed to update employee"),
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [employee, formData, isNewEmployee, toast, createEmployee, createProfile, updateProfile, setUserRole, updateUserRole]);

  return {
    formData,
    saving,
    handleFieldChange,
    handleSubmit
  };
};
